import { useState, useEffect } from 'react';
import Select from '../../../components/select';
import arrow from '../../../assets/svg-icons/blueArrow.svg';
import close from '../../../assets/svg-icons/closeBrand.svg';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Input from '../../../components/input';
import Button from '../../../components/button';
import TextArea from '../../../components/textArea';
import Modal from '../../../components/modal';
import {
  createSecret,
  getSecrets,
  deleteSecret,
  createSecretContainer,
  getSecretContainers,
  deleteSecretContainer,
  addSecretToContainer,
  removeSecretFromContainer,
} from '../../../hooks/local/userReducer';
import { fileToBase64, formatDateMethod } from '../../../hooks/constants';
import { PlusIcon, Trash } from 'lucide-react';

const CertificateManager = () => {
  document.title = 'Certificate Manager - Cloudvantage';

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const projectId = useSelector((state) => state.user.selectedProject)?.id;

  const [createSecretModal, setCreateSecretModal] = useState(false);
  const [createContainerModal, setCreateContainerModal] = useState(false);
  const [manageContainerModal, setManageContainerModal] = useState(false);
  const [addSecretToContainerModal, setAddSecretToContainerModal] =
    useState(false);
  const [selectedSecret, setSelectedSecret] = useState(null);
  const [selectedContainer, setSelectedContainer] = useState(null);
  const [deleteSecretModal, setDeleteSecretModal] = useState(false);
  const [deleteContainerModal, setDeleteContainerModal] = useState(false);
  const [activeTab, setActiveTab] = useState('Overview');

  const [secrets, setSecrets] = useState([]);
  const [containers, setContainers] = useState([]);

  useEffect(() => {
    fetchSecrets();
    fetchContainers();
  }, [projectId]);

  const fetchSecrets = async () => {
    const result = await dispatch(getSecrets(projectId));
    if (result?.payload) setSecrets(result?.payload);
  };

  const fetchContainers = async () => {
    const result = await dispatch(getSecretContainers(projectId));
    if (result?.payload) setContainers(result?.payload);
  };

  const secretTypes = [
    { value: '', label: 'Select secret type' },
    { value: 'certificate', label: 'Certificate' },
    { value: 'private', label: 'Private Key' },
  ];

  const containerTypes = [
    { value: '', label: 'Select container type' },
    { value: 'certificate', label: 'Certificate' },
    { value: 'rsa', label: 'RSA' },
    { value: 'generic', label: 'Generic' },
  ];

  const createSecretForm = useFormik({
    initialValues: {
      name: '',
      secretType: 'certificate',
      expiration: '',
      privateKey: null,
      serverCert: null,
      caChainCert: null,
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      secretType: Yup.string().required('Secret type is required'),
      expiration: Yup.date().required('Expiration date is required'),
      privateKey: Yup.mixed()
        .required('Private key file is required')
        .test('fileFormat', 'File must be a .pem file', (value) =>
          value ? value.name.endsWith('.pem') : false,
        ),
      serverCert: Yup.mixed()
        .required('Server certificate file is required')
        .test('fileFormat', 'File must be a .pem file', (value) =>
          value ? value.name.endsWith('.pem') : false,
        ),
      caChainCert: Yup.mixed()
        .optional()
        .nullable()
        .test('fileFormat', 'File must be a .pem file', (value) =>
          value ? value.name.endsWith('.pem') : true,
        ),
    }),
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        formData.append('name', values.name);
        formData.append('secret_type', values.secretType);
        formData.append('expiration', values.expiration);
        formData.append('payload_content_type', 'application/octet-stream');

        formData.append('payload_content_encoding', 'base64');
        formData.append('private_key', values.privateKey);
        formData.append('server_cert', values.serverCert);

        if (values.caChainCert) {
          formData.append('ca_chain_cert', values.caChainCert);
        }

        const { payload } = await dispatch(
          createSecret({ projectId, secretData: formData }),
        );
        if (payload && payload?.statuscode === '00') {
          fetchSecrets();
          setCreateSecretModal(false);
          createSecretForm.resetForm();
        }
      } catch (error) {
        console.error('Error creating secret:', error);
      }
    },
  });

  const createContainerForm = useFormik({
    initialValues: {
      name: '',
      type: 'certificate',
      certificate: '',
      privateKey: '',
      intermediate: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      type: Yup.string().required('Type is required'),
      certificate: Yup.string().required('Certificate is required'),
      privateKey: Yup.string().required('Private Key is required'),
      intermediate: Yup.string().optional(),
    }),
    onSubmit: async (values) => {
      // Build secret_refs array based on selected values
      const secretRefs = [];

      if (values.certificate) {
        const certificateSecret = secrets.find(
          (secret) => secret.name === values.certificate,
        );
        if (certificateSecret) {
          secretRefs.push({
            name: 'certificate',
            secret_ref: certificateSecret.secret_ref,
          });
        }
      }

      if (values.privateKey) {
        const privateKeySecret = secrets.find(
          (secret) => secret.name === values.privateKey,
        );
        if (privateKeySecret) {
          secretRefs.push({
            name: 'private_key',
            secret_ref: privateKeySecret.secret_ref,
          });
        }
      }

      if (values.intermediate) {
        const intermediateSecret = secrets.find(
          (secret) => secret.name === values.intermediate,
        );
        if (intermediateSecret) {
          secretRefs.push({
            name: 'intermediates',
            secret_ref: intermediateSecret.secret_ref,
          });
        }
      }

      const containerData = {
        name: values.name,
        type: values.type,
        secret_refs: secretRefs,
      };

      console.log(containerData);

      const { payload } = await dispatch(
        createSecretContainer({ projectId, containerData }),
      );
      if (payload && payload?.statuscode === '00') {
        fetchContainers();
        setCreateContainerModal(false);
      }
    },
  });

  const secretsWithTypeCertificate = secrets.filter(
    (secret) => secret?.secret_type === 'certificate',
  );

  const certificateOptions = [
    { value: '', label: 'Select certificate' },
    ...secretsWithTypeCertificate.map((secret) => ({
      value: secret?.name,
      label: secret?.name,
    })),
  ];

  const secretsWithTypePrivate = secrets.filter(
    (secret) => secret?.secret_type === 'private',
  );

  const prrivateKeyOptions = [
    { value: '', label: 'Select private key' },
    ...secretsWithTypePrivate.map((secret) => ({
      value: secret?.name,
      label: secret?.name,
    })),
  ];

  const secretsWithTypeIntermediate = secrets.filter(
    (secret) => secret?.secret_type === 'certificate',
  );

  const intermediateOptions = [
    { value: '', label: 'Select intermediate' },
    ...secretsWithTypeIntermediate
      .filter(
        (secret) => secret?.name !== createContainerForm.values.certificate,
      )
      .map((secret) => ({
        value: secret?.name,
        label: secret?.name,
      })),
  ];

  const addSecretToContainerForm = useFormik({
    initialValues: {
      secretName: '',
      secretRef: '',
    },
    validationSchema: Yup.object({
      secretName: Yup.string().required('Secret name is required'),
      secretRef: Yup.string().required('Secret reference is required'),
    }),
    onSubmit: async (values) => {
      const data = { name: values.secretName, secret_ref: values.secretRef };
      const { payload } = await dispatch(
        addSecretToContainer({
          projectId,
          containerId: selectedContainer,
          data,
        }),
      );
      if (payload && payload?.statuscode === '00') {
        fetchContainers();
        setAddSecretToContainerModal(false);
      }
    },
  });

  const handleDeleteSecret = async (secretId) => {
    await dispatch(deleteSecret({ projectId, secretId }));
    fetchSecrets();
    setDeleteSecretModal(false);
  };

  const handleDeleteContainer = async (containerId) => {
    await dispatch(deleteSecretContainer({ projectId, containerId }));
    fetchContainers();
    setDeleteContainerModal(false);
  };

  const handleAddSecretToContainer = async (
    containerId,
    secretName,
    secretRef,
  ) => {
    const data = { name: secretName, secret_ref: secretRef };
    await dispatch(addSecretToContainer({ projectId, containerId, data }));
    fetchContainers();
  };

  const handleRemoveSecretFromContainer = async (
    containerId,
    secretName,
    secretRef,
  ) => {
    const data = { name: secretName, secret_ref: secretRef };
    await dispatch(removeSecretFromContainer({ projectId, containerId, data }));
    fetchContainers();
  };

  // const tabs = ["Overview", "Secrets", "Containers"];
  const tabs = ['Overview', 'Secrets'];

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <p className="text-xl font-medium text-primaryBlue">
        Certificate Manager
      </p>

      <div className="mt-4">
        <div className="flex border-b dark:border-[#c4c4c432]">
          {tabs.map((tab) => (
            <button
              key={tab}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === tab
                  ? 'border-b-2 border-primaryBlue text-primaryBlue'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'Overview' && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-[#141D26] p-6 rounded-md shadow border dark:border-[#c4c4c432]">
              <h3 className="text-lg font-semibold text-primaryBlue">
                Total Secrets
              </h3>
              <p className="text-3xl font-bold mt-2">{secrets?.length || 0}</p>
            </div>
            {/* <div className="bg-white dark:bg-[#141D26] p-6 rounded-md shadow border dark:border-[#c4c4c432]">
              <h3 className="text-lg font-semibold text-primaryBlue">Total Containers</h3>
              <p className="text-3xl font-bold mt-2">{containers?.length || 0}</p>
            </div> */}
          </div>
        )}

        {activeTab === 'Secrets' && (
          <>
            <div className="w-full flex justify-between items-center gap-4 lg:flex-row flex-col mb-10 mt-6">
              <div className="w-full md:w-[initial] grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                <div className="md:col-span-3">
                  <Select
                    label="Filter Secrets"
                    name="secrets"
                    options={[{ value: 'all-secrets', label: 'All Secrets' }]}
                  />
                </div>
                <div className="md:col-span-4">
                  <input
                    type="search"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search for secrets..."
                    className="bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] focus:outline-none placeholder:opacity-70 placeholder:text-xs dark:bg-slate-800 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex gap-4">
                <div
                  className="px-4 py-3 text-xs border-[1.5px] border-primaryBlue rounded-md text-primaryBlue cursor-pointer"
                  onClick={() => setCreateSecretModal(true)}
                >
                  Create Secret
                </div>
              </div>
            </div>

            <div className="overflow-x-auto rounded-md border dark:border-[#c4c4c432]">
              <table className="w-full text-sm text-left">
                <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border text-gray-400 dark:border-[#c4c4c432] border-b text-sm font-semibold">
                  <tr>
                    <th scope="col" className="px-6 py-4">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-4">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-4">
                      Expiration
                    </th>
                    <th scope="col" className="px-6 py-4">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-slate-800 px-3 md:px-4 border dark:border-[#c4c4c432] border-t-2 text-sm">
                  {secrets?.length > 0 &&
                    secrets
                      ?.filter((s) =>
                        s?.name
                          ?.toLowerCase()
                          ?.includes(searchText?.toLowerCase()),
                      )
                      ?.map((secret, index) => (
                        <tr
                          key={index}
                          className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25"
                        >
                          <td className="px-6 py-4">{secret?.name}</td>
                          <td className="px-6 py-4">{secret?.secret_type}</td>
                          <td className="px-6 py-4 text-nowrap">
                            {formatDateMethod(secret?.expiration)}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              className="text-white p-2 text-xs rounded bg-red-500 mr-2"
                              onClick={() => {
                                setSelectedSecret(secret?.secret_id);
                                setDeleteSecretModal(true);
                              }}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === 'Containers' && (
          <>
            <div className="w-full flex justify-between items-center gap-4 lg:flex-row flex-col mb-10 mt-6">
              <div className="w-full md:w-[initial] grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                <div className="md:col-span-3">
                  <Select
                    label="Filter Containers"
                    name="containers"
                    options={[
                      { value: 'all-containers', label: 'All Containers' },
                    ]}
                  />
                </div>
                <div className="md:col-span-4">
                  <input
                    type="search"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search for containers..."
                    className="bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] focus:outline-none placeholder:opacity-70 placeholder:text-xs dark:bg-slate-800 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex gap-4">
                <div
                  className="px-4 py-3 text-xs border-[1.5px] border-primaryBlue rounded-md text-primaryBlue cursor-pointer"
                  onClick={() => setCreateContainerModal(true)}
                >
                  Create Container
                </div>
              </div>
            </div>

            <div className="overflow-x-auto rounded-md border dark:border-[#c4c4c432]">
              <table className="w-full text-sm text-left">
                <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border text-gray-400 dark:border-[#c4c4c432] border-b text-sm font-semibold">
                  <tr>
                    <th scope="col" className="px-6 py-4">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-4">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-4">
                      Secrets
                    </th>
                    <th scope="col" className="px-6 py-4">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-slate-800 px-3 md:px-4 border dark:border-[#c4c4c432] border-t-2 text-sm">
                  {containers?.length > 0 &&
                    containers?.map((container, index) => (
                      <tr
                        key={index}
                        className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25"
                      >
                        <td className="px-6 py-4">{container?.name}</td>
                        <td className="px-6 py-4">{container?.type}</td>
                        <td className="px-6 py-4">
                          {container?.secret_refs?.map((ref, i) => (
                            <div key={i}>
                              {ref?.name}
                              {container?.secret_refs?.length - 1 !== i
                                ? ','
                                : ''}
                            </div>
                          ))}
                        </td>
                        <td className="px-6 py-4 flex items-center gap-4">
                          <button
                            className="text-white p-3 text-xs rounded bg-primaryBlue w-full md:w-fit"
                            onClick={() => {
                              setSelectedContainer(container?.container_id);
                              setManageContainerModal(true);
                            }}
                          >
                            Manage
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Manage Container Modal */}
      {manageContainerModal && (
        <div className="w-full fixed h-screen bg-black/70 top-0 left-0 px-4 flex items-end md:items-center justify-center z-50">
          <div className="bg-white dark:bg-[#141D26] rounded w-full md:w-[30%] dark:text-white max-h-[80%] mb-4 md:mb-0 overflow-auto">
            <div className="flex justify-between items-center text-sm p-6 border-b dark:border-b-[#c4c4c432]">
              <div className="flex gap-2 items-center">
                <span className="dark:text-white text-black font-medium">
                  Manage "
                  {containers.find((c) => c.id === selectedContainer)?.name ||
                    'Container'}
                  "
                </span>
              </div>
              <img
                src={close}
                onClick={() => setManageContainerModal(false)}
                className="h-5 cursor-pointer"
                alt=""
              />
            </div>
            <div className="p-4 grid gap-1">
              <div
                className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all text-black font-medium cursor-pointer"
                onClick={() => {
                  setSelectedContainer(selectedContainer);
                  setAddSecretToContainerModal(true);
                  setManageContainerModal(false);
                }}
              >
                <PlusIcon className="h-4 text-primaryBlue" />
                <span className="pt-[2px]">Add Secret</span>
                <span>
                  <img src={arrow} alt="" className="" />
                </span>
              </div>
              <div
                onClick={() => {
                  setSelectedContainer(selectedContainer);
                  setDeleteContainerModal(true);
                  setManageContainerModal(false);
                }}
                className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all text-red-500 font-medium cursor-pointer"
              >
                <Trash className="h-4 text-red-500" />
                <span className="pt-[2px] truncate">Delete Container</span>
                <span>
                  <img src={arrow} alt="" className="" />
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Secret Modal */}
      {createSecretModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-1/3 max-h-[80%] overflow-y-scroll">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Create a Secret</p>
              <span
                onClick={() => {
                  setCreateSecretModal(false);
                  createSecretForm.resetForm();
                }}
                className="cursor-pointer"
              >
                <img alt="" src={close} className="h-5" />
              </span>
            </div>
            <form className="gap-y-6 grid">
              <Input
                label="Name"
                type="text"
                name="name"
                value={createSecretForm.values.name}
                onChange={createSecretForm.handleChange}
                onBlur={createSecretForm.handleBlur}
                onError={
                  createSecretForm.errors.name && createSecretForm.touched.name
                    ? createSecretForm.errors.name
                    : null
                }
              />
              <Select
                label="Secret Type"
                name="secretType"
                options={secretTypes}
                value={createSecretForm.values.secretType}
                onChange={createSecretForm.handleChange}
                onBlur={createSecretForm.handleBlur}
                onError={
                  createSecretForm.errors.secretType &&
                  createSecretForm.touched.secretType
                    ? createSecretForm.errors.secretType
                    : null
                }
              />
              <Input
                label="Expiration Date"
                type="date"
                name="expiration"
                value={createSecretForm.values.expiration}
                onChange={createSecretForm.handleChange}
                onBlur={createSecretForm.handleBlur}
                onError={
                  createSecretForm.errors.expiration &&
                  createSecretForm.touched.expiration
                    ? createSecretForm.errors.expiration
                    : null
                }
              />
              <Input
                label="Private Key (.pem)"
                type="file"
                name="privateKey"
                accept=".pem"
                onChange={(event) => {
                  createSecretForm.setFieldValue(
                    'privateKey',
                    event.currentTarget.files[0],
                  );
                }}
                onBlur={createSecretForm.handleBlur}
                onError={
                  createSecretForm.errors.privateKey &&
                  createSecretForm.touched.privateKey
                    ? createSecretForm.errors.privateKey
                    : null
                }
              />
              <Input
                label="Server Certificate (.pem)"
                type="file"
                name="serverCert"
                accept=".pem"
                onChange={(event) => {
                  createSecretForm.setFieldValue(
                    'serverCert',
                    event.currentTarget.files[0],
                  );
                }}
                onBlur={createSecretForm.handleBlur}
                onError={
                  createSecretForm.errors.serverCert &&
                  createSecretForm.touched.serverCert
                    ? createSecretForm.errors.serverCert
                    : null
                }
              />
              <Input
                label="CA Chain Certificate (.pem)"
                type="file"
                name="caChainCert"
                accept=".pem"
                onChange={(event) => {
                  createSecretForm.setFieldValue(
                    'caChainCert',
                    event.currentTarget.files[0],
                  );
                }}
                onBlur={createSecretForm.handleBlur}
                onError={
                  createSecretForm.errors.caChainCert &&
                  createSecretForm.touched.caChainCert
                    ? createSecretForm.errors.caChainCert
                    : null
                }
              />
              <div className="flex justify-between items-center gap-2">
                <Button
                  buttonText="Create Secret"
                  buttonRole="submit"
                  handleClick={(e) => {
                    e.preventDefault();
                    createSecretForm.handleSubmit(e);
                  }}
                  isLoading={createSecretForm.isSubmitting}
                />
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Container Modal */}
      {createContainerModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-1/3 max-h-[80%] overflow-y-scroll">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Create a Container</p>
              <span
                onClick={() => {
                  setCreateContainerModal(false);
                  createContainerForm.resetForm();
                }}
                className="cursor-pointer"
              >
                <img alt="" src={close} className="h-5" />
              </span>
            </div>
            <form className="gap-y-6 grid">
              <Input
                label="Container Name"
                name="name"
                value={createContainerForm.values.name}
                onChange={createContainerForm.handleChange}
                onBlur={createContainerForm.handleBlur}
                onError={
                  createContainerForm.errors.name &&
                  createContainerForm.touched.name
                    ? createContainerForm.errors.name
                    : null
                }
              />
              <Input
                label="Type"
                name="type"
                value={createContainerForm.values.type}
                disabled={true}
                onChange={createContainerForm.handleChange}
                onBlur={createContainerForm.handleBlur}
                onError={
                  createContainerForm.errors.type &&
                  createContainerForm.touched.type
                    ? createContainerForm.errors.type
                    : null
                }
              />
              <Select
                label="Certificate"
                name="certificate"
                options={certificateOptions}
                value={createContainerForm.values.certificate}
                onChange={createContainerForm.handleChange}
                onBlur={createContainerForm.handleBlur}
                onError={
                  createContainerForm.errors.certificate &&
                  createContainerForm.touched.certificate
                    ? createContainerForm.errors.certificate
                    : null
                }
              />

              <Select
                label="Private Key"
                isLabelIcon={true}
                iconTitle="Select matching private key for the certificate"
                name="privateKey"
                options={prrivateKeyOptions}
                value={createContainerForm.values.privateKey}
                onChange={createContainerForm.handleChange}
                onBlur={createContainerForm.handleBlur}
                onError={
                  createContainerForm.errors.privateKey &&
                  createContainerForm.touched.privateKey
                    ? createContainerForm.errors.privateKey
                    : null
                }
              />

              <Select
                label="Intermediate"
                name="intermediate"
                options={intermediateOptions}
                value={createContainerForm.values.intermediate}
                onChange={createContainerForm.handleChange}
                onBlur={createContainerForm.handleBlur}
                onError={
                  createContainerForm.errors.intermediate &&
                  createContainerForm.touched.intermediate
                    ? createContainerForm.errors.intermediate
                    : null
                }
              />

              <div className="flex justify-end items-center gap-2 border-t pt-4 mt-4">
                <Button
                  buttonText="Create Container"
                  buttonRole="submit"
                  handleClick={(e) => {
                    e.preventDefault();
                    console.log(createContainerForm.errors);
                    createContainerForm.handleSubmit(e);
                  }}
                  isLoading={createContainerForm.isSubmitting}
                />
              </div>
            </form>
          </div>
        </div>
      )}

      {addSecretToContainerModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-1/3 max-h-[80%] overflow-y-scroll">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Add Secret to Container</p>
              <span
                onClick={() => {
                  setAddSecretToContainerModal(false);
                  addSecretToContainerForm.resetForm();
                }}
                className="cursor-pointer"
              >
                <img alt="" src={close} className="h-5" />
              </span>
            </div>
            <form className="gap-y-6 grid">
              <Select
                label="Secret Name"
                name="secretName"
                options={[
                  { value: '', label: 'Select secret name' },
                  { value: 'private_key', label: 'Private Key' },
                  { value: 'certificate', label: 'Certificate' },
                  {
                    value: 'private_key_passphrase',
                    label: 'Private Key Passphrase',
                  },
                  { value: 'intermediates', label: 'Intermediates' },
                ]}
                value={addSecretToContainerForm.values.secretName}
                onChange={addSecretToContainerForm.handleChange}
                onBlur={addSecretToContainerForm.handleBlur}
                onError={
                  addSecretToContainerForm.errors.secretName &&
                  addSecretToContainerForm.touched.secretName
                    ? addSecretToContainerForm.errors.secretName
                    : null
                }
              />

              <Input
                label="Secret Reference"
                type="text"
                name="secretRef"
                value={addSecretToContainerForm.values.secretRef}
                onChange={addSecretToContainerForm.handleChange}
                onBlur={addSecretToContainerForm.handleBlur}
                onError={
                  addSecretToContainerForm.errors.secretRef &&
                  addSecretToContainerForm.touched.secretRef
                    ? addSecretToContainerForm.errors.secretRef
                    : null
                }
              />
              <div className="flex justify-between items-center gap-2">
                <Button
                  buttonText="Add Secret"
                  buttonRole="submit"
                  handleClick={(e) => {
                    e.preventDefault();
                    addSecretToContainerForm.handleSubmit(e);
                  }}
                  isLoading={addSecretToContainerForm.isSubmitting}
                />
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Secret Modal */}
      {deleteSecretModal && (
        <Modal
          title="Delete Secret"
          isVisible={deleteSecretModal}
          onClose={() => setDeleteSecretModal(false)}
        >
          <div className="text-sm text-center pt-6">
            Are you sure you want to delete this secret? This action cannot be
            undone.
          </div>
          <div className="grid gap-4 justify-center py-6">
            <button
              className="bg-red-500 text-white text-xs px-6 py-4 rounded-sm"
              onClick={() => handleDeleteSecret(selectedSecret)}
            >
              Yes, delete
            </button>
            <button
              className="text-[#1DA1F2] text-sm px-6 py-2 hover:underline"
              onClick={() => setDeleteSecretModal(false)}
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* Delete Container Modal */}
      {deleteContainerModal && (
        <Modal
          title="Delete Container"
          isVisible={deleteContainerModal}
          onClose={() => setDeleteContainerModal(false)}
        >
          <div className="text-sm text-center pt-6">
            Are you sure you want to delete this container? This action cannot
            be undone.
          </div>
          <div className="grid gap-4 justify-center py-6">
            <button
              className="bg-red-500 text-white text-xs px-6 py-4 rounded-sm"
              onClick={() => handleDeleteContainer(selectedContainer)}
            >
              Yes, delete
            </button>
            <button
              className="text-[#1DA1F2] text-sm px-6 py-2 hover:underline"
              onClick={() => setDeleteContainerModal(false)}
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CertificateManager;
